import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Phone, Mail, MapPin, Facebook, Instagram } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ChespiRetailLogo } from "@/components/Logo";
import { useLang } from "@/i18n";

const pub = (f) => `${process.env.PUBLIC_URL}/${f}`;
const SITE = "https://complexdiplomatic.ro/liquor-store-mamaia/";

export default function RetailPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const r = t.retail;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const backHome = () => {
    navigate("/");
    setTimeout(() => document.getElementById("sectors")?.scrollIntoView({ behavior: "smooth" }), 90);
  };

  return (
    <main className="bg-tobacco">
      {/* Hero — text left, logo right */}
      <section className="relative overflow-hidden bg-tobacco pb-12 pt-32 md:pt-40">
        {/* Storefront interior — blurred backdrop behind the hero copy */}
        <div className="pointer-events-none absolute inset-0">
          <img
            src={pub("liqourstore.jpeg")}
            alt=""
            aria-hidden="true"
            className="h-full w-full scale-110 object-cover object-center opacity-40 blur-[6px]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-tobacco/85 via-tobacco/75 to-tobacco" />
        </div>
        <div className="pointer-events-none absolute right-0 top-1/4 h-[60vh] w-[60vh] translate-x-1/3 rounded-full bg-gold/10 blur-3xl" />

        <div className="container-luxe relative grid items-center gap-10 md:grid-cols-2 md:gap-14">
          <div>
            <Reveal>
              <ChespiRetailLogo
                markSize={48}
                lionClass="h-9 w-9 sm:h-11 sm:w-11 lg:h-12 lg:w-12"
                textClass="text-base sm:text-2xl lg:text-3xl"
                frameClass="border-[3px] px-2.5 py-1 sm:px-4 sm:py-1.5"
              />
              <p className="mt-5 max-w-xl font-serif text-xl italic text-gold-leaf/90 sm:text-2xl">
                {r.aboutLead}
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-7 max-w-xl space-y-4 text-sm leading-relaxed text-white/65 md:text-base">
                <p>{r.p1}</p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href={SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:bg-gold-light"
              >
                {r.cta} <ArrowUpRight size={16} />
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="order-first md:order-last">
            <img
              src={pub("LiquorStore.png")}
              alt={r.brand}
              className="mx-auto w-full max-w-sm drop-shadow-[0_12px_50px_rgba(212,175,55,0.25)] md:max-w-md"
            />
          </Reveal>
        </div>
      </section>

      {/* Categories — alternating editorial layout */}
      <section className="relative bg-tobacco pb-16 pt-4 md:pb-24">
        <div className="container-luxe">
          <Reveal className="mb-12">
            <p className="overline">{r.categoriesTitle}</p>
          </Reveal>

          <div className="space-y-16 md:space-y-24">
            {r.categories.map((c, i) => {
              const flip = i % 2 === 1;
              return (
                <Reveal key={c.name}>
                  <div
                    className={`flex flex-col gap-8 md:flex-row md:items-center md:gap-14 ${
                      flip ? "md:flex-row-reverse" : ""
                    }`}
                  >
                    <div className="md:w-2/5">
                      <div className="relative flex items-center justify-center overflow-hidden border border-gold/15 bg-tobacco-surface p-8">
                        <div className="pointer-events-none absolute inset-0 bg-gold/[0.04]" />
                        <img
                          src={pub(c.img)}
                          alt={c.name}
                          loading="lazy"
                          className="relative max-h-72 w-auto object-contain transition-transform duration-700 ease-out hover:scale-105"
                        />
                      </div>
                    </div>
                    <div className={`md:w-3/5 ${flip ? "md:text-right" : ""}`}>
                      <span className="font-serif text-sm tracking-[0.3em] text-gold/50">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div className={`mt-3 h-px w-10 bg-gold/50 ${flip ? "md:ml-auto" : ""}`} />
                      <h3 className="mt-4 font-serif text-3xl text-white">{c.name}</h3>
                      <p
                        className={`mt-4 max-w-lg text-sm leading-relaxed text-white/65 md:text-base ${
                          flip ? "md:ml-auto md:text-right" : ""
                        }`}
                      >
                        {c.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Location — "Close to you", now a large feature with the storefront photo */}
      <section className="relative bg-tobacco-surface py-24 md:py-32">
        <div className="container-luxe">
          <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
            <Reveal>
              <span className="mb-6 block h-px w-12 bg-gold/50" />
              <h2 className="font-serif text-3xl font-light leading-snug text-white sm:text-4xl md:text-5xl">
                {r.locationTitle}
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/70 md:text-lg">
                {r.locationText}
              </p>
              <div className="mt-8 flex items-center gap-2 text-sm tracking-wide text-gold-leaf/90">
                <MapPin size={16} className="text-gold" /> Mamaia, Constanța
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="relative overflow-hidden border border-gold/15 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.75)]">
                <img
                  src={pub("liqourstore.jpeg")}
                  alt={r.brand}
                  loading="lazy"
                  className="h-[22rem] w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105 md:h-[30rem]"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-tobacco/50 to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="section-pad relative bg-tobacco">
        <div className="container-luxe">
          <Reveal className="mx-auto max-w-xl text-center">
            <p className="overline mb-4">{r.contactTitle}</p>
            <h2 className="font-serif text-3xl font-light text-white sm:text-4xl">{r.brand}</h2>

            <div className="mt-8 flex flex-col items-center gap-4 text-sm text-white/70">
              <a href="tel:+40790000326" className="flex items-center gap-2 transition-colors hover:text-gold">
                <Phone size={16} className="text-gold" /> +40 790 000 326
              </a>
              <a href="tel:0723909090" className="flex items-center gap-2 transition-colors hover:text-gold">
                <Phone size={16} className="text-gold" /> 0723 909090
              </a>
              <a
                href="mailto:diplomaticvanzari@gmail.com"
                className="flex items-center gap-2 transition-colors hover:text-gold"
              >
                <Mail size={16} className="text-gold" /> diplomaticvanzari@gmail.com
              </a>
              <span className="flex items-center gap-2">
                <MapPin size={16} className="text-gold" /> Mamaia, Constanța
              </span>
            </div>

            <div className="mt-7 flex items-center justify-center gap-4">
              <a
                href="https://www.facebook.com/Diplomatic-Mamaia-1827526164232452"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-gold/30 p-3 text-gold/80 transition-all hover:border-gold hover:text-gold"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="https://www.instagram.com/diplomaticmamaia/"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-gold/30 p-3 text-gold/80 transition-all hover:border-gold hover:text-gold"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
            </div>

            <button
              onClick={backHome}
              className="mt-12 inline-flex items-center gap-2 border border-gold/40 px-6 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-all duration-300 hover:bg-gold hover:text-ink"
            >
              <ArrowLeft size={16} /> {r.back}
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
